import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  AppBar,
  Toolbar,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DatePicker from "../../../Components/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";
import { generateInvoicePDF } from "../../../utils/pdfGenerator";

const AddInvoiceModal = ({
  open,
  onClose,
  handleSave,
  tableApis,
  clientApis,
  financeSettingsApi,
  mode = "add", // "add" or "edit"
  invoiceData = null, // Data for edit mode
}) => {
  // Pre-add invoice data state
  const [preAddData, setPreAddData] = useState(null);
  const [loadingPreAdd, setLoadingPreAdd] = useState(false);

  // Invoice number parts
  const [invoicePrefix, setInvoicePrefix] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    invoiceDate: dayjs(),
    dueDate: dayjs().add(15, "day"),
    currency: null, // Will be set from API
    client: null,
    paymentDetails: null, // Will store payment method ID
    billingAddress: "",
    shippingAddress: "",
    generatedBy: "RPTPL",
    noteForRecipient: "",
    termsAndConditions: "",
  });

  // Client search and data
  const [clients, setClients] = useState([]);
  const [clientSearchValue, setClientSearchValue] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);

  // Currency data
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // Payment methods data
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Tax data and selection
  const [taxes, setTaxes] = useState([]);
  const [selectedTaxes, setSelectedTaxes] = useState({});

  // PDF preview state
  const [pdfPreview, setPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // RCM and state code fields
  const [isGSTPayableUnderRCM, setIsGSTPayableUnderRCM] = useState(false);
  const [stateCode, setStateCode] = useState("");

  const [items, setItems] = useState([
    {
      description: "",
      detailedDescription: "",
      file: null,
      quantity: 1,
      unit: "Pcs",
      unitPrice: 0,
      amount: 0,
    },
  ]);

  const [totals, setTotals] = useState({
    subTotal: 0,
    discount: 0,
    discountType: "%",
    tax: 0,
    total: 0,
  });

  // Fetch pre-add invoice data
  const fetchPreAddData = useCallback(async () => {
    if (!tableApis || !tableApis.preAddInvoice) return;

    try {
      setLoadingPreAdd(true);
      const response = await tableApis.preAddInvoice();
      console.log("Pre-add invoice data:", response);

      setPreAddData(response);

      // Set invoice number parts
      const prefix = response.finance_settings?.invoice_prefix + response.finance_settings?.invoice_start_separator || "";
      const nextId = response.next_invoice_id || "";
      setInvoicePrefix(prefix);
      setInvoiceNumber(nextId);

      // Set currency with default handling
      if (response.currencies && Array.isArray(response.currencies)) {
        setCurrencies(response.currencies);
        const defaultCurrency = response.currencies.find((curr) => curr.is_default === 1) || response.currencies[0];
        if (defaultCurrency) {
          setFormData((prev) => ({ ...prev, currency: defaultCurrency.id }));
        }
      }

      // Set payment methods
      if (response.payment_settings && Array.isArray(response.payment_settings)) {
        setPaymentMethods(response.payment_settings);
      }

      // Set taxes
      if (response.tax && Array.isArray(response.tax)) {
        setTaxes(response.tax);
        // Initialize all taxes as unchecked
        const initialTaxSelection = {};
        response.tax.forEach((tax) => {
          initialTaxSelection[tax.id] = false;
        });
        setSelectedTaxes(initialTaxSelection);
      }

      // Set clients (limit to 10 initially)
      if (response.clients && Array.isArray(response.clients)) {
        const formattedClients = response.clients.slice(0, 10).map((client) => ({
          id: client.id,
          user_id: client.user_id,
          name: client.company_name || client.user_id,
          company_name: client.company_name,
          username: client.username,
          email: client.email,
          image: client.image,
          company_address: client.company_address,
          state: client.state,
          tax_number: client.tax_number,
        }));
        setClients(formattedClients);
      }

      // Set dates and terms based on API
      if (response.finance_settings) {
        const today = dayjs();
        setFormData((prev) => ({
          ...prev,
          invoiceDate: today,
          dueDate: today.add(response.finance_settings.due_after || 15, "day"),
          termsAndConditions: response.finance_settings.Terms || "",
        }));
      }

    } catch (error) {
      console.error("Error fetching pre-add invoice data:", error);
    } finally {
      setLoadingPreAdd(false);
    }
  }, [tableApis]);

  // Fetch only reference data (clients, currencies, taxes, payment methods) without default values
  const fetchReferenceData = useCallback(async () => {
    if (!tableApis || !tableApis.preAddInvoice) return;

    try {
      setLoadingPreAdd(true);
      const response = await tableApis.preAddInvoice();
      console.log("Reference data for edit mode:", response);

      setPreAddData(response);

      // Set currencies (but don't set default)
      if (response.currencies && Array.isArray(response.currencies)) {
        setCurrencies(response.currencies);
      }

      // Set payment methods
      if (response.payment_settings && Array.isArray(response.payment_settings)) {
        setPaymentMethods(response.payment_settings);
      }

      // Set taxes (but don't initialize as unchecked)
      if (response.tax && Array.isArray(response.tax)) {
        setTaxes(response.tax);
      }

      // Set clients (limit to 10 initially)
      if (response.clients && Array.isArray(response.clients)) {
        const formattedClients = response.clients.slice(0, 10).map((client) => ({
          id: client.id,
          user_id: client.user_id,
          name: client.company_name || client.user_id,
          company_name: client.company_name,
          username: client.username,
          email: client.email,
          image: client.image,
          company_address: client.company_address,
          state: client.state,
          tax_number: client.tax_number,
        }));
        setClients(formattedClients);
      }

    } catch (error) {
      console.error("Error fetching reference data:", error);
    } finally {
      setLoadingPreAdd(false);
    }
  }, [tableApis]);

  // Fetch clients with filter
  const fetchClients = useCallback(
    async (searchTerm = "") => {
      if (!preAddData?.clients) return;

      try {
        setLoadingClients(true);
        const allClients = preAddData.clients;

        // Filter clients based on search term
        const filteredClients = allClients.filter((client) => {
          const searchLower = searchTerm.toLowerCase();
          return (
            client.user_id?.toLowerCase().includes(searchLower) ||
            client.username?.toLowerCase().includes(searchLower) ||
            client.company_name?.toLowerCase().includes(searchLower) ||
            client.email?.toLowerCase().includes(searchLower)
          );
        });

        const formattedClients = filteredClients.slice(0, 10).map((client) => ({
          id: client.id,
          user_id: client.user_id,
          name: client.company_name || client.user_id,
          company_name: client.company_name,
          username: client.username,
          email: client.email,
          image: client.image,
          company_address: client.company_address,
          state: client.state,
          tax_number: client.tax_number,
        }));

        setClients(formattedClients);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    },
    [preAddData]
  );

  // Load data on component mount or when invoiceData changes
  useEffect(() => {
    if (open) {
      if (mode === "add") {
        fetchPreAddData();
      } else if (mode === "edit") {
        // Populate form with existing invoice data
        populateFormDataFromInvoice();
      }
    }
  }, [open, mode, invoiceData, fetchPreAddData, fetchReferenceData]);

  // Populate form data from existing invoice
  const populateFormDataFromInvoice = useCallback(async () => {
    if (!invoiceData) return;

    try {
      console.log("Populating form with invoice data:", invoiceData);

      // Set invoice number parts
      const fullInvoiceNumber = invoiceData.invoice_number || "";
      const prefixMatch = fullInvoiceNumber.match(/^([A-Z]+-\d+)(\d+)$/);
      if (prefixMatch) {
        setInvoicePrefix(prefixMatch[1]);
        setInvoiceNumber(prefixMatch[2]);
      } else {
        setInvoicePrefix("");
        setInvoiceNumber(fullInvoiceNumber);
      }

      // Set form data
      setFormData({
        invoiceDate: invoiceData.Invoice_date ? dayjs(invoiceData.Invoice_date) : dayjs(),
        dueDate: invoiceData.due_date ? dayjs(invoiceData.due_date) : dayjs().add(15, "day"),
        currency: invoiceData.currency || null,
        client: null, // Will be set after fetching clients
        paymentDetails: invoiceData.payment_details || null,
        billingAddress: invoiceData.billing_address || "",
        shippingAddress: invoiceData.shipping_address || "",
        generatedBy: invoiceData.Generated_by || "RPTPL",
        noteForRecipient: invoiceData.note || "",
        termsAndConditions: invoiceData.terms || "",
      });

      // Set items
      if (invoiceData.items && Array.isArray(invoiceData.items)) {
        const formattedItems = invoiceData.items.map(item => ({
          description: item.item || item.description || "",
          detailedDescription: item.description || "",
          file: null,
          quantity: item.quantity || 1,
          unit: item.quality_type === 1 ? "Pcs" : item.quality_type === 2 ? "Hours" : "Days",
          unitPrice: item.unit_price || item.price || 0,
          amount: item.amount || 0,
        }));
        setItems(formattedItems);
      }

      // Set totals
      setTotals({
        subTotal: invoiceData.sub_total || invoiceData.total_amount || 0,
        discount: invoiceData.discount || 0,
        discountType: invoiceData.discount_id === 1 ? "%" : "Amount",
        tax: invoiceData.tax_amount || 0,
        total: invoiceData.total_amount || 0,
      });

      // Set RCM and state code if available
      setIsGSTPayableUnderRCM(invoiceData.is_gst_payable_under_rcm || false);
      setStateCode(invoiceData.state_code || "");

      // Fetch only reference data (clients, currencies, taxes) for edit mode
      await fetchReferenceData();

      // Set client after clients are loaded
      setTimeout(() => {
        if (clients.length > 0 && invoiceData.client_id) {
          const client = clients.find(c => c.user_id === invoiceData.client_id || c.id === invoiceData.client_id);
          if (client) {
            handleClientChange(null, client);
          }
        }

        // Set selected taxes if available
        if (invoiceData.tax && Array.isArray(invoiceData.tax)) {
          const newTaxSelection = {};
          taxes.forEach(tax => {
            newTaxSelection[tax.id] = invoiceData.tax.includes(tax.id);
          });
          setSelectedTaxes(newTaxSelection);
        }
      }, 500);

    } catch (error) {
      console.error("Error populating form data:", error);
    }
  }, [invoiceData, fetchReferenceData, clients, taxes]);

  // Recalculate totals when taxes change
  useEffect(() => {
    if (items.length > 0) {
      calculateTotals(items);
    }
  }, [selectedTaxes, taxes]);

  // Handle client search
  const handleClientSearch = (searchTerm) => {
    setClientSearchValue(searchTerm);
    if (searchTerm.length >= 2 || searchTerm === "") {
      fetchClients(searchTerm);
    }
  };

  // Handle client change and auto-fill billing address
  const handleClientChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, client: newValue }));

    if (newValue) {
      // Auto-fill billing address
      const billingAddress = newValue.company_address
        ? `${newValue.company_address}${newValue.state ? `, ${newValue.state}` : ""}`
        : "";
      setFormData((prev) => ({
        ...prev,
        client: newValue,
        billingAddress: billingAddress,
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Calculate amount for the item
    if (field === "quantity" || field === "unitPrice") {
      newItems[index].amount =
        newItems[index].quantity * newItems[index].unitPrice;
    }

    setItems(newItems);
    calculateTotals(newItems);
  };

  const handleItemFileChange = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const newItems = [...items];
      newItems[index].file = file;
      setItems(newItems);
    }
  };

  const handleItemFileRemove = (index) => {
    const newItems = [...items];
    newItems[index].file = null;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        detailedDescription: "",
        file: null,
        quantity: 1,
        unit: "Pcs",
        unitPrice: 0,
        amount: 0,
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    calculateTotals(newItems);
  };

  const calculateTotals = (itemsArray) => {
    const subTotal = itemsArray.reduce((sum, item) => sum + item.amount, 0);

    // Calculate discount
    let discountAmount = 0;
    if (totals.discountType === "%") {
      discountAmount = (subTotal * totals.discount) / 100;
    } else {
      discountAmount = totals.discount;
    }

    // Calculate tax for selected taxes (always calculate after discount)
    let taxAmount = 0;
    taxes.forEach((tax) => {
      if (selectedTaxes[tax.id]) {
        const taxableAmount = subTotal - discountAmount;
        taxAmount += (taxableAmount * parseFloat(tax.rate)) / 100;
      }
    });

    const total = subTotal - discountAmount + taxAmount;

    setTotals((prev) => ({
      ...prev,
      subTotal,
      tax: taxAmount,
      total,
    }));
  };

  // Handle tax checkbox change
  const handleTaxCheckboxChange = (taxId, checked) => {
    setSelectedTaxes((prev) => ({
      ...prev,
      [taxId]: checked,
    }));
    // Recalculate totals
    calculateTotals(items);
  };

  // Handle tax rate change
  const handleTaxRateChange = (taxId, newRate) => {
    setTaxes((prev) =>
      prev.map((tax) => (tax.id === taxId ? { ...tax, rate: newRate } : tax))
    );
    // Recalculate totals
    calculateTotals(items);
  };

  // Handle PDF preview
  const handlePdfPreview = async () => {
    // Validation: Check if client is selected
    if (!formData.client) {
      alert("Please select a client before previewing the invoice.");
      return;
    }

    try {
      // Map client data to invoice format
      const clientData = formData.client || {};
      const clientName = clientData.company_name || clientData.user_id || "N/A";
      const clientAddress = clientData.company_address || "N/A";
      const clientState = clientData.state || "N/A";
      const clientStateCode = clientData.tax_number || "N/A";

      // Map payment details
      const paymentName = paymentMethods?.find((p) => p.id === formData.paymentDetails)?.title || "N/A";
      const paymentDescription = paymentMethods?.find((p) => p.id === formData.paymentDetails)?.description || "N/A";
      const accountNumber = preAddData?.finance_settings?.bank_account_number || "N/A";
      const ifsccCode = preAddData?.finance_settings?.bank_ifsc_code || "N/A";
      const branch = preAddData?.finance_settings?.bank_branch || "N/A";

      // Get company settings for header
      const companyName = preAddData?.finance_settings?.company_name || "N/A";
      const companyAddress = preAddData?.finance_settings?.company_address || "N/A";
      const companyState = preAddData?.finance_settings?.company_state || "N/A";
      const companyEmail = preAddData?.finance_settings?.company_email || "N/A";
      const customerCareNumber = preAddData?.finance_settings?.customer_care_number || "N/A";

      // Generate PDF with sample format structure
      const invoiceData = {
        companySettings: preAddData?.finance_settings,
        invoice_number: invoicePrefix + invoiceNumber,
        invoiceNumber: invoicePrefix + invoiceNumber,
        Invoice_date: formData.invoiceDate ? formData.invoiceDate.format("YYYY-MM-DD") : "",
        invoice_date: formData.invoiceDate ? formData.invoiceDate.format("YYYY-MM-DD") : "",
        due_date: formData.dueDate ? formData.dueDate.format("YYYY-MM-DD") : "",
        client_name: clientName,
        username: clientData.username || clientData.user_id || "N/A",
        shipping_address: formData.shippingAddress || clientAddress,
        state: clientState,
        state_code: clientStateCode,
        client_gstin: clientStateCode,
        client: {
          name: clientName,
          address: clientAddress,
          email: companyEmail,
          customerCareNumber: customerCareNumber,
        },
        items: items.map((item) => ({
          name: item.description || "N/A",
          description: item.detailedDescription || "N/A",
          item_name: item.description || "N/A",
          quantity: item.quantity,
          qty: item.quantity,
          unit_price: item.unitPrice,
          price: item.unitPrice,
          rate: item.unitPrice,
        })),
        taxes: taxes.filter((tax) => selectedTaxes[tax.id]),
        totals: totals,
        billingAddress: formData.billingAddress || "N/A",
        generatedBy: "RPTPL",
        termsAndConditions: formData.termsAndConditions,
        isGSTPayableUnderRCM: isGSTPayableUnderRCM,
        paymentDetails: {
          name: paymentName,
          description: paymentDescription,
          accountNumber: accountNumber,
          ifsccCode: ifsccCode,
          branch: branch,
        },
        createdDate: dayjs().format("YYYY-MM-DD"),
        authorizedBy: "RPTPL",
        total_amount: totals.total,
        currency: formData.currency,
        paymentDetailsId: formData.paymentDetails,
      };

      console.log("Generating PDF with data:", invoiceData);

      // Generate PDF
      const pdfBlob = await generateInvoicePDF(invoiceData);
      console.log("PDF Generated:", pdfBlob);

      // Create blob URL from the PDF blob
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(blobUrl);
      setPdfPreview(true);
    } catch (error) {
      console.error("Error generating PDF popup preview:", error);
      alert("Failed to generate PDF popup preview. Please try again.");
    }
  };

  // Clean up blob URL when modal closes
  const handleClosePdfPreview = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfPreview(false);
  };

  const handleSaveInvoice = async () => {
    try {
      // Validation: Check if client is selected
      if (!formData.client) {
        alert("Please select a client before saving the invoice.");
        return;
      }

      // Debug: Check client data
      console.log("Selected client:", formData.client);
      console.log("Client user_id:", formData.client?.user_id);
      console.log("Client numeric id:", formData.client?.id);
      console.log(
        "Will send client_id:",
        formData.client?.user_id || formData.client?.id
      );

      // Format data to match the required JSON structure
      const fullInvoiceNumber = `${invoicePrefix}${invoiceNumber}`;

      // Get selected tax IDs
      const selectedTaxIds = taxes.filter((tax) => selectedTaxes[tax.id]).map((tax) => tax.id);

      const invoiceData = {
        client_id: formData.client?.user_id || formData.client?.id, // Try user_id first, then id
        invoice_number: fullInvoiceNumber,
        Invoice_date: formData.invoiceDate.format("YYYY-MM-DD"),
        due_date: formData.dueDate.format("YYYY-MM-DD"),
        currency: parseInt(formData.currency), // Ensure currency is a number
        shipping_address: formData.shippingAddress || "",
        discount_id: totals.discountType === "%" ? 1 : 2, // 1 for percentage, 2 for amount
        payment_details: parseInt(formData.paymentDetails) || 1, // Ensure it's a number
        paid_date: dayjs().format("YYYY-MM-DD"),
        created_by: "1602", // Default value - could be from user session
        note: formData.noteForRecipient,
        terms: formData.termsAndConditions,
        discount: parseFloat(totals.discount) || 0,
        total_amount: parseFloat(totals.total) || 0,
        paid_amount: 0, // Default value
        paid_on: dayjs().format("YYYY-MM-DD"),
        save_status: 1,
        invoice_to: 1, // Default value
        authorized_by: "EMPID", // Default value - could be from user session
        Generated_by: formData.generatedBy,
        tax: selectedTaxIds, // Send selected tax IDs
        items: items.map((item) => ({
          item: item.description,
          description: item.detailedDescription,
          quality_type: item.unit === "Pcs" ? 1 : item.unit === "Hours" ? 2 : 3, // Map unit to quality_type
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unitPrice) || 0,
          amount: parseFloat(item.amount) || 0,
        })),
      };

      console.log("Formatted Invoice Data:", invoiceData);

      let response;
      // Call the API based on mode
      if (mode === "add") {
        if (tableApis && tableApis.add) {
          response = await tableApis.add(invoiceData);
          console.log("Invoice added successfully:", response);
        } else {
          console.error("tableApis.add is not available");
        }
      } else if (mode === "edit") {
        if (tableApis && tableApis.update && invoiceData) {
          response = await tableApis.update(invoiceData.invoice_number, invoiceData);
          console.log("Invoice updated successfully:", response);
        } else {
          console.error("tableApis.update is not available or no invoiceData provided");
        }
      }

      // Call the parent's handleSave if provided
      if (handleSave && response) {
        handleSave(response);
      }

      onClose();
    } catch (error) {
      console.error("Error saving invoice:", error);
      // You might want to show an error message to the user here
    }
  };

  // Common input styling inspired by reference
  const inputStyles = {
    bgcolor: "#ffffff",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: "#bdbdbd",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1976d2",
      },
    },
    "& .MuiInputBase-input": {
      color: "#000000",
      "&::placeholder": {
        color: "#9e9e9e",
        opacity: 1,
      },
    },
    "& .MuiSelect-select": {
      color: "#000000",
    },
    "& .MuiInputAdornment-root": {
      color: "#000000",
    },
  };

  // Add dropdown menu styling
  const MenuProps = {
    PaperProps: {
      sx: {
        bgcolor: "#ffffff",
        "& .MuiMenuItem-root": {
          color: "#000000",
          bgcolor: "#ffffff",
          "&:hover": {
            bgcolor: "#f5f5f5",
          },
          "&.Mui-selected": {
            bgcolor: "#e3f2fd",
            "&:hover": {
              bgcolor: "#bbdefb",
            },
          },
        },
      },
    },
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "75%",
            bgcolor: "#ffffff",
            color: "#000000",
          },
        }}
      >
        {/* Header */}
        <AppBar
          position="sticky"
          sx={{
            bgcolor: "#ffffff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ color: "#000000", fontWeight: 600 }}>
              {mode === "add" ? "New Invoice" : "Edit Invoice"}
            </Typography>
            <IconButton onClick={onClose} sx={{ color: "#000000" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Box
          sx={{
            p: 3,
            bgcolor: "#ffffff",
            color: "#212121",
            height: "100%",
            overflowY: "auto",
          }}
        >
          {/* Invoice Details Section */}
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "#000000" }}
          >
            Invoice Details
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Invoice Number <span style={{ color: "red" }}>*</span>
              </Typography>
              {mode === "edit" ? (
                <TextField
                  fullWidth
                  value={invoiceData?.invoice_number || `${invoicePrefix}${invoiceNumber}`}
                  size="small"
                  sx={{
                    bgcolor: "#f5f5f5",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "& .MuiInputBase-input": {
                        color: "#000000",
                        fontWeight: 500,
                        WebkitTextFillColor: "#000000",
                      },
                      "& .Mui-disabled": {
                        "& .MuiOutlinedInput-input": {
                          color: "#000000",
                          WebkitTextFillColor: "#000000",
                        },
                      },
                    },
                  }}
                  disabled
                />
              ) : (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography
                    sx={{
                      color: "#000000",
                      fontWeight: 500,
                      fontSize: "14px",
                      width: { xs: "60%", md: "50%" },
                    }}
                  >
                    {invoicePrefix}
                  </Typography>
                  <TextField
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    size="small"
                    sx={{
                      bgcolor: "#ffffff",
                      width: { xs: "40%", md: "50%" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#000000",
                        },
                        "&:hover fieldset": {
                          borderColor: "#000000",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#000000",
                        fontWeight: 500,
                        "&::placeholder": {
                          color: "#9e9e9e",
                          opacity: 1,
                        },
                      },
                    }}
                    placeholder="00001"
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Invoice Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.invoiceDate}
                  onChange={(date) => handleInputChange("invoiceDate", date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: inputStyles,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Due Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.dueDate}
                  onChange={(date) => handleInputChange("dueDate", date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: inputStyles,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Currency
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.currency || ""}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                  sx={{
                    bgcolor: "#ffffff",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#000000",
                      },
                      "&:hover fieldset": {
                        borderColor: "#000000",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#000000",
                      },
                    },
                    "& .MuiSelect-select": {
                      color: "#000000",
                    },
                  }}
                  MenuProps={MenuProps}
                  disabled={loadingPreAdd || currencies.length === 0}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.id} value={currency.id}>
                      {currency.currency_code}({currency.currency_symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Client <span style={{ color: "red" }}>*</span>
              </Typography>
              {mode === "edit" && formData.client ? (
                <TextField
                  fullWidth
                  value={formData.client?.company_name || formData.client?.user_id || "N/A"}
                  size="small"
                  sx={{
                    bgcolor: "#f5f5f5",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "& .MuiInputBase-input": {
                        color: "#000000",
                        WebkitTextFillColor: "#000000",
                      },
                      "& .Mui-disabled": {
                        "& .MuiOutlinedInput-input": {
                          color: "#000000",
                          WebkitTextFillColor: "#000000",
                        },
                      },
                    },
                  }}
                  disabled
                />
              ) : (
                <Autocomplete
              value={formData.client}
              onChange={handleClientChange}
              inputValue={clientSearchValue}
              onInputChange={(event, newInputValue) => {
                handleClientSearch(newInputValue);
              }}
              options={clients}
              getOptionLabel={(option) =>
                option?.company_name || option?.user_id || ""
              }
              isOptionEqualToValue={(option, value) =>
                option?.id === value?.id
              }
              loading={loadingClients}
              disabled={mode === "view"}

              // ✅ THE REAL FIX
              slotProps={{
                popper: {
                  sx: {
                    "& .MuiPaper-root": {
                      backgroundColor: "#ffffff",
                      color: "#000000",
                    },
                    "& .MuiAutocomplete-listbox": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiAutocomplete-option": {
                      backgroundColor: "#ffffff",
                      color: "#000000",
                    },
                    "& .MuiAutocomplete-option:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiAutocomplete-option.Mui-focused": {
                      backgroundColor: "#e3f2fd",
                    },
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                      backgroundColor: "#e3f2fd",
                      color: "#000000",
                    },
                  },
                },
              }}

              // ✅ Keep input styling simple
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search clients..."
                  sx={{
                    bgcolor: "#ffffff",
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#ffffff",
                      "& fieldset": {
                        borderColor: "#000000",
                      },
                      "&:hover fieldset": {
                        borderColor: "#000000",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#000000",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#000000",
                    },
                  }}
                />
              )}

              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      
                      {/* Avatar */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#1976d2",
                        }}
                      >
                        {option.image && option.image !== "non" ? (
                          <img
                            src={option.image}
                            alt={option.username}
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          (option.username || "N/A").charAt(0).toUpperCase()
                        )}
                      </Box>

                      {/* Info */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontWeight: "bold", color: "#1976d2", fontSize: "14px" }}>
                          {option.company_name || option.user_id}
                        </Box>
                        <Box sx={{ fontSize: "12px", color: "#666", mt: 0.5 }}>
                          {option.username || "N/A"}
                        </Box>
                        <Box sx={{ fontSize: "11px", color: "#888", mt: 0.3 }}>
                          {option.email || "N/A"}
                        </Box>
                      </Box>

                    </Box>
                  </Box>
                </li>
              )}

              noOptionsText="No clients found"
              loadingText="Loading clients..."
            />
              )}
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Payment Details
              </Typography>
              <Autocomplete
                value={
                  paymentMethods.find((m) => m.id === formData.paymentDetails) || null
                }
                onChange={(event, newValue) => {
                  handleInputChange(
                    "paymentDetails",
                    newValue ? newValue.id : null
                  );
                }}
                options={paymentMethods}
                getOptionLabel={(option) => option?.title || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }

                // ✅ Force ALL layers white
                slotProps={{
                  popper: {
                    sx: {
                      "& .MuiPaper-root": {
                        backgroundColor: "#fff !important",
                        color: "#000 !important"
                      },
                      "& .MuiAutocomplete-listbox": {
                        backgroundColor: "#fff !important"
                      },
                      "& .MuiAutocomplete-option": {
                        backgroundColor: "#fff !important",
                        color: "#000 !important"
                      },
                      "& .MuiAutocomplete-option:hover": {
                        backgroundColor: "#f5f5f5 !important"
                      },
                      "& .MuiAutocomplete-option.Mui-focused": {
                        backgroundColor: "#e3f2fd !important"
                      },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        backgroundColor: "#e3f2fd !important",
                        color: "#000 !important"
                      }
                    }
                  }
                }}

                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select payment method..."
                    sx={{
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#fff",
                        "& fieldset": { borderColor: "#000" },
                        "&:hover fieldset": { borderColor: "#000" },
                        "&.Mui-focused fieldset": { borderColor: "#000" }
                      },
                      "& .MuiInputBase-input": {
                        color: "#000"
                      }
                    }}
                  />
                )}

                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: "100%" }}>
                      <Box sx={{ fontWeight: "bold", fontSize: "14px" }}>
                        {option.title}
                      </Box>
                      {option.description && (
                        <Box sx={{ fontSize: "12px", color: "#666", mt: 0.5 }}>
                          {option.description}
                        </Box>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Grid>
          </Grid>

          {/* Address Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Billing Address
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.billingAddress}
                onChange={(e) =>
                  handleInputChange("billingAddress", e.target.value)
                }
                size="small"
                sx={{
                  ...inputStyles,
                  "& .MuiInputBase-input": { color: "#757575" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Shipping Address
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Select the client to show billing address."
                value={formData.shippingAddress}
                onChange={(e) =>
                  handleInputChange("shippingAddress", e.target.value)
                }
                size="small"
                sx={{
                  ...inputStyles,
                  "& .MuiInputBase-input::placeholder": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {/* Is GST Payable under RCM checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isGSTPayableUnderRCM}
                    onChange={(e) => setIsGSTPayableUnderRCM(e.target.checked)}
                    size="small"
                      sx={{
                      color: "#000000",
                      "&.Mui-checked": {
                        color: "#000000",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "14px", color: "#000000" }}>
                    Is GST Payable under RCM
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          {/* Items Table */}
          <TableContainer
            component={Paper}
            sx={{
              mb: 3,
              bgcolor: "white",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Table>
              <TableHead sx={{ bgcolor: "#f9f9f9" }}>
                <TableRow>
                  <TableCell sx={{ color: "#424242", fontWeight: "bold" }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ color: "#424242", fontWeight: "bold" }}>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ color: "#424242", fontWeight: "bold" }}>
                    Unit Price
                  </TableCell>
                  <TableCell sx={{ color: "#424242", fontWeight: "bold" }}>
                    Amount
                  </TableCell>
                  <TableCell width="50px"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <React.Fragment key={index}>
                    <TableRow sx={{ bgcolor: "white" }}>
                      <TableCell>
                        <TextField
                          fullWidth
                          placeholder="Item Name"
                          value={item.description}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "description",
                              e.target.value
                            )
                          }
                          size="small"
                                  sx={{
                            bgcolor: "#ffffff",
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#000000",
                              },
                              "&:hover fieldset": {
                                borderColor: "#000000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#000000",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "#000000",
                              "&::placeholder": {
                                color: "#9e9e9e",
                                opacity: 1,
                              },
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <TextField
                            type="number"
                            value={item.quantity === 0 ? "" : item.quantity}
                            placeholder="0"
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            size="small"
                                      sx={{
                              width: 80,
                              bgcolor: "#ffffff",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "#000000",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#000000",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#000000",
                                },
                              },
                              "& .MuiInputBase-input": {
                                color: "#000000",
                                "&::placeholder": {
                                  color: "#9e9e9e",
                                  opacity: 1,
                                },
                              },
                            }}
                          />
                          <FormControl size="small" sx={{ minWidth: 60 }}>
                            <Select
                              value={item.unit}
                              onChange={(e) =>
                                handleItemChange(index, "unit", e.target.value)
                              }
                                          sx={{
                                bgcolor: "#ffffff",
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "#000000",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#000000",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#000000",
                                  },
                                },
                                "& .MuiSelect-select": {
                                  color: "#000000",
                                },
                              }}
                              MenuProps={MenuProps}
                            >
                              <MenuItem value="Pcs">Pcs</MenuItem>
                              <MenuItem value="Hours">Hours</MenuItem>
                              <MenuItem value="Days">Days</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.unitPrice === 0 ? "" : item.unitPrice}
                          placeholder="0"
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "unitPrice",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          size="small"
                                  sx={{
                            bgcolor: "#ffffff",
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#000000",
                              },
                              "&:hover fieldset": {
                                borderColor: "#000000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#000000",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "#000000",
                              "&::placeholder": {
                                color: "#9e9e9e",
                                opacity: 1,
                              },
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={item.amount.toFixed(2)}
                          InputProps={{ readOnly: true }}
                          size="small"
                          sx={{
                            ...inputStyles,
                            bgcolor: "#f0f0f0",
                            "& .MuiInputBase-input": { color: "#424242" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => removeItem(index)}
                          size="small"
                          sx={{ color: "#757575" }}
                                >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: "white" }}>
                      <TableCell colSpan={6}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={7}>
                            <TextField
                              fullWidth
                              placeholder="Enter Description (optional)"
                              value={item.detailedDescription}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "detailedDescription",
                                  e.target.value
                                )
                              }
                              size="small"
                                          sx={{
                                ...inputStyles,
                                "& .MuiInputBase-input::placeholder": {
                                  color: "#9e9e9e",
                                  opacity: 1,
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            {item.file ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="caption" sx={{ color: "#212121", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "150px" }}>
                                  {item.file.name}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleItemFileRemove(index)}
                                  sx={{ color: "#d32f2f" }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                              sx={{
                                  borderColor: "#e0e0e0",
                                  color: "#000000",
                                  "&:hover": {
                                    borderColor: "#1976d2",
                                    color: "#1976d2",
                                  },
                                  textTransform: "none",
                                  height: "32px",
                                }}
                              >
                                Upload File
                                <input
                                  type="file"
                                  hidden
                                  onChange={(e) => handleItemFileChange(index, e)}
                                />
                              </Button>
                            )}
                          </Grid>
                          <Grid item xs={2}></Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            onClick={addItem}
            variant="text"
            startIcon={<AddIcon />}
            sx={{ mb: 3, color: "#1976d2" }}
            disabled={mode === "view"}
          >
            Add Item
          </Button>

          {/* Totals Section */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <Box
              sx={{
                minWidth: 600,
                bgcolor: "white",
                p: 3,
                borderRadius: 1,
                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
              }}
            >
              {/* Sub Total */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography sx={{ color: "#000000", fontWeight: 500 }}>
                  Sub Total
                </Typography>
                <Typography sx={{ color: "#000000", fontWeight: 500 }}>
                  {totals.subTotal.toFixed(2)}
                </Typography>
              </Box>

              {/* Discount */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography sx={{ color: "#000000", minWidth: 80 }}>
                  Discount
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                  <TextField
                    type="number"
                    value={totals.discount === 0 ? "" : totals.discount}
                    placeholder="0"
                    onChange={(e) => {
                      const newDiscount = parseFloat(e.target.value) || 0;
                      setTotals((prev) => ({
                        ...prev,
                        discount: newDiscount,
                      }));
                      calculateTotals(items);
                    }}
                    size="small"
                      sx={{
                      width: 80,
                      bgcolor: "#ffffff",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#000000",
                        },
                        "&:hover fieldset": {
                          borderColor: "#000000",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#000000",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#000000",
                        "&::placeholder": {
                          color: "#9e9e9e",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  <FormControl size="small">
                    <Select
                      value={totals.discountType}
                      onChange={(e) => {
                        setTotals((prev) => ({
                          ...prev,
                          discountType: e.target.value,
                        }));
                        calculateTotals(items);
                      }}
                          sx={{
                        bgcolor: "#ffffff",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#000000",
                          },
                          "&:hover fieldset": {
                            borderColor: "#000000",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#000000",
                          },
                        },
                        "& .MuiSelect-select": {
                          color: "#000000",
                        },
                      }}
                      MenuProps={MenuProps}
                    >
                      <MenuItem value="%">%</MenuItem>
                      <MenuItem value="Amount">Amount</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Typography sx={{ color: "#000000", ml: 2, minWidth: 100, textAlign: "right" }}>
                  {totals.discountType === "%"
                    ? ((totals.subTotal * totals.discount) / 100).toFixed(2)
                    : totals.discount.toFixed(2)}
                </Typography>
              </Box>

              {/* Taxes */}
              {taxes.length > 0 && (
                <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid #e0e0e0" }}>
                  <Typography sx={{ color: "#000000", mb: 1 }}>
                    Tax
                  </Typography>
                  {taxes.map((tax) => (
                    <Box
                      key={tax.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                        <Checkbox
                          checked={selectedTaxes[tax.id] || false}
                          onChange={(e) =>
                            handleTaxCheckboxChange(tax.id, e.target.checked)
                          }
                          size="small"
                                  sx={{
                            color: "#000000",
                            "&.Mui-checked": {
                              color: "#000000",
                            },
                          }}
                        />
                        <Typography sx={{ fontSize: "14px", color: "#000000", minWidth: 60 }}>
                          {tax.tax_name}
                        </Typography>
                        <TextField
                          type="number"
                          value={tax.rate === 0 ? "" : tax.rate}
                          placeholder="0"
                          onChange={(e) =>
                            handleTaxRateChange(tax.id, e.target.value)
                          }
                          size="small"
                          sx={{
                            width: 80,
                            bgcolor: "#ffffff",
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#000000",
                              },
                              "&:hover fieldset": {
                                borderColor: "#000000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#000000",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "#000000",
                              "&::placeholder": {
                                color: "#9e9e9e",
                                opacity: 1,
                              },
                            },
                          }}
                          disabled={!selectedTaxes[tax.id]}
                          InputProps={{
                            endAdornment: (
                              <Typography sx={{ fontSize: "12px", color: "#000000" }}>
                                %
                              </Typography>
                            ),
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: selectedTaxes[tax.id] ? "#000000" : "#9e9e9e",
                          ml: 2,
                          minWidth: 100,
                          textAlign: "right",
                          fontSize: "14px",
                        }}
                      >
                        {selectedTaxes[tax.id]
                          ? ((totals.subTotal -
                              (totals.discountType === "%"
                                ? (totals.subTotal * totals.discount) / 100
                                : totals.discount)) *
                              parseFloat(tax.rate)) /
                            100
                          : 0.0}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Total */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: "bold",
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                <Typography fontWeight="bold" sx={{ color: "#000000", fontSize: "16px" }}>
                  Total
                </Typography>
                <Typography
                  fontWeight="bold"
                  sx={{ color: "#1976d2", fontSize: "18px" }}
                >
                  {totals.total.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Notes Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                note for the recipient
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g. Thank you for your business"
                value={formData.noteForRecipient}
                onChange={(e) =>
                  handleInputChange("noteForRecipient", e.target.value)
                }
                size="small"
                sx={{
                  ...inputStyles,
                  "& .MuiInputBase-input::placeholder": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, color: "#000000", fontWeight: 500 }}>
                Terms and Conditions
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  minHeight: "75px",
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                  color: "#000000",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  border: "1px solid #e0e0e0",
                }}
              >
                {formData.termsAndConditions || "No terms and conditions available"}
              </Box>
            </Grid>
          </Grid>

          {/* Footer Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
              bgcolor: "white",
              p: 2,
              borderRadius: 1,
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Button
              variant="contained"
              onClick={handleSaveInvoice}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                bgcolor: "#1976d2",
                color: "white",
                "&:hover": { bgcolor: "#1565c0" },
              }}
            >
              {mode === "add" ? "Save" : "Update"}
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: "#757575",
                borderColor: "#e0e0e0",
                "&:hover": {
                  borderColor: "#bdbdbd",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Save As Draft
            </Button>
            <Button
              variant="text"
              onClick={handlePdfPreview}
              sx={{ color: "#1976d2" }}
            >
              PDF Preview
            </Button>
            <Button variant="text" onClick={onClose} sx={{ color: "#757575" }}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* PDF Preview Modal */}
      <Dialog
        open={pdfPreview}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
            bgcolor: "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: "#000000", fontWeight: 600 }}>
            Invoice Preview - {invoicePrefix}{invoiceNumber}
          </Typography>
          <IconButton onClick={handleClosePdfPreview} sx={{ color: "#000000" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#ffffff", height: "calc(90vh - 120px)" }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title="PDF Preview"
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "#f5f5f5",
            borderTop: "1px solid #e0e0e0",
            p: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              if (pdfUrl) {
                const fullInvoiceNumber = invoicePrefix + invoiceNumber;
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `Invoice_${fullInvoiceNumber}.pdf`;
                link.click();
              }
            }}
            sx={{
              bgcolor: "#1976d2",
              color: "white",
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            onClick={handleClosePdfPreview}
            sx={{
              color: "#000000",
              borderColor: "#e0e0e0",
              "&:hover": {
                borderColor: "#bdbdbd",
                bgcolor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddInvoiceModal;
